import { useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ARENA_SCORING } from '../../types/arena';
import type { ArenaGameResult } from '../../types/arena';
import GameEndOverlay from '../game/GameEndOverlay';
import { log } from '../../utils/logger';
import './ArenaGameEndOverlay.css';

interface ArenaGameEndOverlayProps {
  result: ArenaGameResult;
  /** Perché la partita è finita, già tradotto. */
  reasonText?: string;
  /**
   * Cosa vuol dire lo stato dell'arena per la PROSSIMA partita.
   *
   * `'draining'` e' il tempo finito con l'arena ancora `'active'`: le partite
   * cominciate prima del termine si giocano fino in fondo, quindi la riga a
   * database resta aperta, ma nessun altro accoppiamento verra' fatto. Senza
   * questo terzo valore il riquadro offriva «Continua» — un bottone il cui
   * unico esito e' un rifiuto del server — e chi lo premeva restava a fissare
   * «cerco un avversario» fino all'ultima partita di tutta l'arena.
   *
   * Tre valori di un solo campo e non un booleano a parte: sono tre risposte
   * che si escludono, e un flag separato renderebbe scrivibile la coppia senza
   * senso «finita e in svuotamento».
   */
  arenaStatus: 'active' | 'draining' | 'finished';
  /** «Continua»: si torna online e si riprende a cercare avversari. */
  onPlayNext: () => void;
  /**
   * La chiave i18n dell'esito, quando «Continua» non ha funzionato.
   *
   * `resumeSearch` col socket giu' scriveva l'errore in `error` dello store,
   * che rende SOLO la pagina di dettaglio dell'arena: qui sopra non cambiava
   * niente, e il bottone si premeva all'infinito senza effetto e senza una
   * parola. L'esito ora arriva fin qui.
   */
  playNextErrorKey?: string | null;
  /** «In pausa»: si resta fermi e si torna alla pagina dell'arena. */
  onPause: () => void;
  /**
   * Il RITORNO sta arrivando: epoch ms in cui comincia, o `null`.
   *
   * Quando c'è, questo riquadro cambia natura. Non è più «sei fermo, riparti
   * quando vuoi»: la partita successiva è già decisa, è già aperta lato server
   * e il suo orologio cammina. I due bottoni spariscono — non c'è niente da
   * scegliere — e al loro posto resta il numero che scende, che è l'unico
   * avviso che il giocatore riceve prima di ritrovarsi sulla damiera.
   */
  ritornoStartsAt?: number | null;
}

/*
 * Il riepilogo non se ne va da solo.
 *
 * Qui c'era un conto alla rovescia di due secondi che allo zero rimetteva in
 * coda: non bastavano a leggere punti, posizione e serie, e ci si ritrovava
 * nella partita dopo. Adesso la fine partita mette in pausa (la scrive il
 * server, `chiudiFinePartita`) e questo riquadro resta finché non lo si chiude
 * con uno dei due bottoni. Niente timer significa anche niente WCAG 2.2.1 da
 * disattivare: non c'è più nessun limite di tempo da fermare.
 */
export default function ArenaGameEndOverlay({
  result,
  reasonText,
  arenaStatus,
  onPlayNext,
  onPause,
  playNextErrorKey = null,
  ritornoStartsAt = null,
}: ArenaGameEndOverlayProps) {
  const { t } = useTranslation();

  /*
   * I secondi che mancano al ritorno, arrotondati per eccesso: a 0.2 secondi
   * dalla fine si legge ancora «1», che è quello che un umano si aspetta —
   * arrotondando per difetto lo zero resterebbe a schermo per un secondo
   * intero prima che la damiera compaia.
   *
   * Non fa partire niente: a far partire il ritorno è il server, e lo store
   * consuma la partita in coda da sé. Questo numero è solo il racconto.
   */
  const [secondiAlRitorno, setSecondiAlRitorno] = useState<number | null>(null);
  useEffect(() => {
    if (ritornoStartsAt === null) {
      setSecondiAlRitorno(null);
      return;
    }
    const aggiorna = () => setSecondiAlRitorno(Math.max(0, Math.ceil((ritornoStartsAt - Date.now()) / 1000)));
    aggiorna();
    const id = setInterval(aggiorna, 250);
    return () => clearInterval(id);
  }, [ritornoStartsAt]);

  const attesaRitorno = ritornoStartsAt !== null;

  // Log result data on mount
  useEffect(() => {
    log.info('ArenaGameEndOverlay', 'Overlay shown', { result: result.result, points: result.pointsEarned, rank: result.newRank });
  }, [result]);

  const handlePlayNow = useCallback(() => {
    onPlayNext();
  }, [onPlayNext]);

  // Calculate base points from result (before boost multiplier)
  const basePoints = result.result === 'win'
    ? ARENA_SCORING.WIN
    : result.result === 'draw'
      ? ARENA_SCORING.DRAW
      : ARENA_SCORING.LOSS;

  /* Il boost raddoppia solo la vittoria: stessa regola del server
     (`ARENA_SCORING`, ramo `win`). */
  const conBoost = result.boostBonus && result.result === 'win';
  const totaleAtteso = conBoost
    ? basePoints * ARENA_SCORING.BOOST_MULTIPLIER
    : basePoints;

  /*
   * La scomposizione si mostra solo se torna.
   *
   * «Punti base» e la riga del boost li ricalcola questo riquadro da cio' che
   * ha in mano; «Totale» viene dal server ed e' la verita'. Le due cose possono
   * ancora dire numeri diversi, e il caso che resta e' **la finestra di
   * rilascio**: un client col codice nuovo contro un server non ancora
   * riavviato riceve `pointsEarned: 3` (la vecchia vittoria in serie) mentre
   * qui si calcola 2. Senza questa guardia il riquadro leggerebbe «Punti base
   * +2» sopra «Totale +3» — una somma sbagliata, sotto gli occhi di chi ha
   * appena finito di giocare. Lo stesso vale a parti invertite, se si tornasse
   * indietro di una versione.
   *
   * Nessuno dei due numeri si tocca: quando non tornano si mostra solo quello
   * che viene dal server, che e' l'unico di cui si risponde. Un dettaglio in
   * meno e' meglio di un dettaglio falso.
   */
  const scomposizioneTorna = totaleAtteso === result.pointsEarned;

  /*
   * Partita senza punti: una riga sola, e nessuna scomposizione.
   *
   * Il riquadro ricalcola i «Punti base» dal TIPO di esito
   * (`ARENA_SCORING.WIN` per una vittoria), mentre il «Totale» lo legge dal
   * server. Quando il server annulla i punti le due cose si contraddicono a
   * schermo: «Punti base +2» sopra «Totale +0», con la posizione ferma e
   * nessuna parola che dica il perche'.
   *
   * Il campo che risolve, `pointsVoided`, il server lo manda dal primo giorno.
   * Non e' piu' una questione di mosse — ogni esito assegna punti, anche a
   * mossa zero — e resta acceso solo quando la registrazione del punteggio e'
   * fallita a meta' e la partita e' stata abbandonata: raro, ma e' proprio il
   * caso in cui una spiegazione serve.
   */
  const statsSlot = (
    <>
      {/* Points breakdown */}
      {result.pointsVoided ? (
        <div className="arena-game-end-points">
          <h4 className="arena-game-end-points-title">
            {t('arena.gameEnd.pointsEarned')}
          </h4>
          <p
            className="arena-game-end-points-voided"
            data-testid="arena-game-end-points-voided"
          >
            {t('arena.gameEnd.pointsVoided')}
          </p>
        </div>
      ) : (
      <div
        className="arena-game-end-points"
        data-testid="arena-game-end-points"
        data-scomposta={scomposizioneTorna ? 'true' : 'false'}
      >
        <h4 className="arena-game-end-points-title">
          {t('arena.gameEnd.pointsEarned')}
        </h4>

        {scomposizioneTorna && (
        <>
        <div className="arena-game-end-points-row" data-testid="arena-game-end-points-row" data-kind="base">
          <span>{t('arena.gameEnd.basePoints')}</span>
          <span className={`arena-game-end-points-value${basePoints > 0 ? ' positive' : ''}`}>
            {basePoints > 0 ? `+${basePoints}` : '0'}
          </span>
        </div>

        {conBoost && (
          <div className="arena-game-end-points-row arena-game-end-animate" data-testid="arena-game-end-points-row" data-kind="boost">
            <span>{t('arena.gameEnd.boostBonus')}</span>
            <span className="arena-game-end-points-value boost">
              {basePoints} ×{ARENA_SCORING.BOOST_MULTIPLIER}
            </span>
          </div>
        )}
        </>
        )}

        <div className="arena-game-end-points-row total" data-testid="arena-game-end-points-row" data-kind="total">
          <span>{t('arena.gameEnd.total')}</span>
          {/* Zero non e' un guadagno: dopo una sconfitta il totale si leggeva
              «+0» e per giunta in verde — la classe `positive` era scritta
              fissa. E' la stessa regola dei «Punti base» dieci righe sopra. */}
          <span className={`arena-game-end-points-value${result.pointsEarned > 0 ? ' positive' : ''}`}>
            {result.pointsEarned > 0 ? `+${result.pointsEarned}` : '0'}
          </span>
        </div>
      </div>
      )}

      {/* Posizione e punti totali: due riquadri, non piu' tre. */}
      <div className="arena-game-end-stats">
        <div className="arena-game-end-stat" data-testid="arena-game-end-stat" data-kind="rank">
          <span
            data-testid="arena-game-end-rank"
            data-move={result.rankDelta < 0 ? 'up' : result.rankDelta > 0 ? 'down' : undefined}
            className={`arena-game-end-stat-value ${
              result.rankDelta < 0 ? 'arena-game-end-rank-slide-up' :
              result.rankDelta > 0 ? 'arena-game-end-rank-slide-down' : ''
            }`}
          >
            {result.newRank != null ? `#${result.newRank}` : '\u2014'}
            {result.newRank != null && result.rankDelta !== 0 && (
              <span
                data-testid="arena-game-end-delta"
                data-direction={result.rankDelta < 0 ? 'up' : 'down'}
                className={`arena-game-end-rank-delta ${result.rankDelta < 0 ? 'up' : 'down'}`}
              >
                {result.rankDelta < 0 ? `\u2191${Math.abs(result.rankDelta)}` : `\u2193${result.rankDelta}`}
              </span>
            )}
          </span>
          <span className="arena-game-end-stat-label">
            {t('arena.gameEnd.rank')}
          </span>
        </div>

        <div className="arena-game-end-stat" data-testid="arena-game-end-stat" data-kind="points">
          <span className="arena-game-end-stat-value arena-game-end-animate" data-testid="arena-game-end-points-total">
            {result.newPoints != null ? result.newPoints : '\u2014'}
          </span>
          <span className="arena-game-end-stat-label">
            {t('arena.gameEnd.totalPoints')}
          </span>
        </div>

      </div>
    </>
  );

  /* Dove stava il numero che scendeva, ora c'è lo stato: sei fermo, e riparti
     quando vuoi. Senza, i due bottoni sarebbero l'unica cosa a dirlo. */
  const countdownSlot = (
    <div className="arena-game-end-countdown" data-testid="arena-game-end-countdown">
      <p
        className="arena-game-end-countdown-text"
        {...(attesaRitorno ? { 'data-testid': 'arena-game-end-return-leg' } : {})}
      >
        {attesaRitorno
          ? t('arena.gameEnd.returnLegIn', { count: secondiAlRitorno ?? 0 })
          : arenaStatus === 'finished'
            ? t('arena.gameEnd.arenaFinished')
            : arenaStatus === 'draining'
              ? t('arena.gameEnd.arenaDraining')
              : t('arena.gameEnd.paused')}
      </p>
      {/* Il posto dove questo riquadro dice in che stato sei e' questo: se
          «Continua» non e' partito, lo stato e' che sei ancora fermo. */}
      {playNextErrorKey && (
        <p
          className="arena-game-end-countdown-text"
          data-testid="arena-game-end-play-next-error"
          role="alert"
        >
          {t(playNextErrorKey)}
        </p>
      )}
    </div>
  );

  /*
   * Ad arena finita non c'è una prossima partita, ma un overlay senza bottoni
   * è un vicolo cieco: restava la damiera congelata sotto e nessun modo di
   * andarsene. `onPlayNext` qui non rimette in coda — lo store vede l'arena
   * chiusa e riporta alla pagina dell'arena.
   */
  /*
   * Nessun bottone durante l'attesa del ritorno: non c'è una scelta da offrire.
   * «In pausa» prometterebbe una cosa che il server non può più concedere — la
   * stanza del ritorno nasce comunque — e «Continua» sarebbe un click che non
   * cambia niente, perché la partita si consuma da sola.
   */
  /*
   * `'draining'` si comporta come `'finished'`: un bottone solo, quello per
   * tornare all'arena. «Continua» lo rifiuterebbe il server e «In pausa»
   * prometterebbe una pausa che nessuna ripresa puo' piu' sciogliere.
   *
   * `attesaRitorno` resta davanti a tutto: con `'draining'` non puo' capitare —
   * `armaRitorno`, lato server, non apre nessun ritorno a tempo scaduto — e
   * l'ordine e' una cintura, non un ramo vivo.
   */
  const actionsSlot = attesaRitorno ? null : arenaStatus !== 'active' ? (
    <div className="arena-game-end-actions">
      <button
        className="arena-game-end-btn play"
        data-testid="arena-game-end-back"
        onClick={handlePlayNow}
        type="button"
      >
        {t('arena.gameEnd.backToArena')}
      </button>
    </div>
  ) : (
    <div className="arena-game-end-actions">
      <button
        className="arena-game-end-btn pause"
        data-testid="arena-game-end-pause"
        onClick={() => {  onPause(); }}
        type="button"
      >
        {t('arena.gameEnd.pause')}
      </button>
      <button
        className="arena-game-end-btn play"
        data-testid="arena-game-end-play-next"
        onClick={handlePlayNow}
        type="button"
      >
        {t('arena.gameEnd.playNext')}
      </button>
    </div>
  );

  return (
    <GameEndOverlay
      resultType={result.result}
      reasonText={reasonText}
      opponentUsername={result.opponentUsername}
      opponentRating={result.opponentRating}
      opponentAvatar={result.opponentAvatar}
      statsSlot={statsSlot}

      countdownSlot={countdownSlot}
      actionsSlot={actionsSlot}
    />
  );
}
